// Página de relatórios
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Breadcrumbs } from '../../components/layout/Breadcrumbs';
import { Download, FileText } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { notasFiscaisMock } from '../../services/mockData';
import { formatCurrency } from '../../utils/formatters';

export function Relatorios() {
  // Dados para gráficos
  const vendasPorMes = [
    { mes: 'Jan', valor: 80000 },
    { mes: 'Fev', valor: 95000 },
    { mes: 'Mar', valor: 110000 },
    { mes: 'Abr', valor: 105000 },
    { mes: 'Mai', valor: 125000 },
    { mes: 'Jun', valor: 140000 },
  ];
  
  const notasPorTipo = [
    { tipo: 'NFe', quantidade: 80, valor: 800000 },
    { tipo: 'NFSe', quantidade: 50, valor: 600000 },
    { tipo: 'NFCe', quantidade: 20, valor: 12000 },
  ];
  
  const topClientes = notasFiscaisMock
    .reduce((acc, nota) => {
      const cliente = acc.find(c => c.nome === nota.destinatario.nome);
      if (cliente) {
        cliente.valor += nota.total;
      } else {
        acc.push({ nome: nota.destinatario.nome, valor: nota.total });
      }
      return acc;
    }, [] as { nome: string; valor: number }[])
    .sort((a, b) => b.valor - a.valor)
    .slice(0, 5);
  
  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Relatórios' }]} />
      
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Relatórios</h1>
      </div>
      
      {/* Filtros */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Filtros do Relatório
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Input label="Data Início" type="date" />
          <Input label="Data Fim" type="date" />
          <Select
            label="Tipo de Nota"
            options={[
              { value: '', label: 'Todos' },
              { value: 'NFe', label: 'NFe' },
              { value: 'NFSe', label: 'NFSe' },
              { value: 'NFCe', label: 'NFCe' },
            ]}
          />
          <div className="flex items-end gap-2">
            <Button variant="primary" className="w-full">
              Gerar Relatório
            </Button>
          </div>
        </div>
      </Card>
      
      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vendas Mensais */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Vendas Mensais
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={vendasPorMes}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
              <XAxis dataKey="mes" className="text-gray-600 dark:text-gray-400" />
              <YAxis className="text-gray-600 dark:text-gray-400" />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="valor" stroke="#0ea5e9" strokeWidth={2} name="Valor (R$)" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        
        {/* Notas por Tipo */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Notas por Tipo
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={notasPorTipo}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
              <XAxis dataKey="tipo" className="text-gray-600 dark:text-gray-400" />
              <YAxis className="text-gray-600 dark:text-gray-400" />
              <Tooltip />
              <Legend />
              <Bar dataKey="quantidade" fill="#0ea5e9" name="Quantidade" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
      
      {/* Top Clientes */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Top 5 Clientes
        </h3>
        <div className="space-y-3">
          {topClientes.map((cliente, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
            >
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold">
                  {index + 1}
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{cliente.nome}</p>
                </div>
              </div>
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                {formatCurrency(cliente.valor)}
              </p>
            </div>
          ))}
        </div>
      </Card>
      
      {/* Ações */}
      <Card>
        <div className="flex gap-4">
          <Button variant="primary" className="flex-1">
            <Download className="w-4 h-4 mr-2" />
            Exportar para Excel
          </Button>
          <Button variant="secondary" className="flex-1">
            <FileText className="w-4 h-4 mr-2" />
            Exportar para PDF
          </Button>
        </div>
      </Card>
    </div>
  );
}
